/**
 * Status Indicator
 * UI component for displaying system status and WebRTC controls
 *
 * Exports:
 * - createStatusButton(): Creates the button element
 * - showNotification(type, customText): Shows status notifications
 * - showWebRTCState(state, text): Shows WebRTC button states
 * - updateButtonOnlineStatus(isOnline): Updates button to online/offline
 * - registerWebRTCClickHandler(handler): Registers click handler for WebRTC controls
 */

let statusButton = null;
let webrtcClickHandler = null;

// All possible notification and button state classes
const ALL_STATE_CLASSES = [
  'notification-connecting',
  'notification-online',
  'notification-offline',
  'notification-disconnected',
  'initial',
  'muted',
  'unmuted'
];

/**
 * Helper function to remove all state classes and add new one
 * @param {string} newClass - New class to add
 */
function updateButtonClass(newClass) {
  if (!statusButton) return;
  statusButton.classList.remove(...ALL_STATE_CLASSES);
  statusButton.classList.add(newClass);
}

/**
 * Helper function to animate button width when text changes
 * Measures current width, changes text, measures new width, then animates
 * @param {HTMLElement} button - Button element
 * @param {string} newText - New text to display
 */
function animateButtonWidth(button, newText) {
  if (!button) return;

  // Remove transition temporarily to measure accurately
  const originalTransition = button.style.transition || '';
  button.style.transition = 'none';

  // Measure current width
  const currentWidth = button.offsetWidth;

  // Change text
  const label = button.querySelector('span');
  if (label) {
    label.textContent = newText;
  } else {
    button.textContent = newText;
  }

  // Measure new width (after text change)
  button.style.width = 'auto';
  const newWidth = button.offsetWidth;

  // Set to old width (starting point)
  button.style.width = `${currentWidth}px`;

  // Force layout recalc before restoring transition
  button.offsetHeight;

  // Restore transition (or remove if originally empty)
  if (originalTransition) {
    button.style.transition = originalTransition;
  } else {
    button.style.transition = '';
  }

  // Trigger animation to new width
  requestAnimationFrame(() => {
    button.style.width = `${newWidth}px`;
  });
}

/**
 * Create status button and append to controls container
 */
export function createStatusButton() {
  const controlsContainer = document.getElementById('controls');
  if (!controlsContainer) {
    console.error('[🔘 Status Indicator] Controls container not found');
    return;
  }

  // Create button element
  statusButton = document.createElement('button');
  statusButton.id = 'audio-control';

  // Create text span
  const textSpan = document.createElement('span');
  statusButton.appendChild(textSpan);

  // Add click handler that delegates to WebRTC if in WebRTC state
  statusButton.addEventListener('click', () => {
    const isWebRTCState = statusButton.classList.contains('initial') ||
                         statusButton.classList.contains('muted') ||
                         statusButton.classList.contains('unmuted');

    if (isWebRTCState && webrtcClickHandler) {
      console.log('[🔘 Status Indicator] Button clicked - delegating to WebRTC handler');
      webrtcClickHandler();
    } else {
      console.log('[🔘 Status Indicator] Button clicked - no WebRTC handler or not in WebRTC state');
    }
  });

  // Append to controls container
  controlsContainer.appendChild(statusButton);

  console.log('[🔘 Status Indicator] Button created and added to controls container');

  // NOTE: Don't show initial notification here - let caller decide initial state
  // This allows different packages to use different initial states (offline, connecting, etc.)

  return statusButton;
}

/**
 * Show notification on button (temporary or permanent)
 * @param {string} type - Notification type: connecting, online, offline, disconnected
 * @param {string} customText - Optional custom text instead of default
 */
export function showNotification(type, customText = null) {
  if (!statusButton) return;

  const notificationTexts = {
    connecting: 'connecting...',
    online: 'maschin online',
    offline: 'maschin offline',
    disconnected: 'disconnected'
  };

  const text = customText || notificationTexts[type] || type;

  // Update button class and animate width
  updateButtonClass(`notification-${type}`);
  animateButtonWidth(statusButton, text);

  console.log(`[🔘 Status Indicator] Showing notification: "${text}"`);
}

/**
 * Show WebRTC button state (start/mute controls)
 * @param {string} state - Button state: initial, muted, unmuted
 * @param {string} text - Button text
 */
export function showWebRTCState(state, text) {
  if (!statusButton) return;

  // Update button class and animate width
  updateButtonClass(state);
  animateButtonWidth(statusButton, text);

  console.log(`[🔘 Status Indicator] WebRTC state: "${state}" - "${text}"`);
}

/**
 * Update button to show online/offline status
 * Also updates the connection status circle
 * @param {boolean} isOnline - Whether system is online
 */
export function updateButtonOnlineStatus(isOnline) {
  if (!statusButton) return;

  console.log(`[🔘 Status Indicator] Update to ${isOnline ? 'ONLINE' : 'OFFLINE'}`);

  // IMPORTANT: Update status circle AND button together
  // This ensures they are always in sync

  // 1. Update status circle first
  const connectionStatus = document.getElementById('connection-status');
  if (connectionStatus) {
    // Make status circle visible (remove hidden class)
    connectionStatus.classList.remove('hidden');

    if (isOnline) {
      connectionStatus.classList.remove('disconnected');
      connectionStatus.classList.add('connected');
    } else {
      connectionStatus.classList.remove('connected');
      connectionStatus.classList.add('disconnected');
    }
    console.log(`[🔘 Status Indicator] Status circle set to ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
  }

  // 2. Then update button
  if (isOnline) {
    showNotification('online');
  } else {
    showNotification('offline');
  }
}

/**
 * Register click handler for WebRTC controls
 * This allows webrtc.js to handle clicks when button is in WebRTC state
 * @param {Function} handler - Click handler function
 */
export function registerWebRTCClickHandler(handler) {
  webrtcClickHandler = handler;
  console.log('[🔘 Status Indicator] WebRTC click handler registered');
}

/**
 * Get button element (for direct DOM access if needed)
 */
export function getButton() {
  return statusButton;
}

/**
 * Initialize button status from backend
 * Fetches current status from Status Backend and displays it
 * Updates BOTH button AND circle
 * Should be called after createStatusButton()
 */
export async function initStatusFromBackend() {
  try {
    console.log('[🔘 Status Indicator] Fetching initial status from backend...');
    const response = await fetch('/api/status');

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('[🔘 Status Indicator] Backend status:', data.online ? 'ONLINE' : 'OFFLINE');

    // Update BOTH button and circle with backend status
    updateButtonOnlineStatus(data.online);

    // ========== NEU: Update Overlay ==========
    if (window.updateOverlayStatus) {
      window.updateOverlayStatus(data.online);
    }
    // =========================================
  } catch (err) {
    console.error('[🔘 Status Indicator] Failed to fetch initial status:', err);
    // Fallback to offline if backend unavailable
    updateButtonOnlineStatus(false);

    // ========== NEU: Overlay zu Offline ==========
    if (window.updateOverlayStatus) {
      window.updateOverlayStatus(false);
    }
    // =============================================
  }
}
